import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/common/Button';
import { toast } from 'sonner';
import { ArrowLeft, Check } from 'lucide-react';
import { PasswordStrength, usePasswordValidation } from '@/components/auth/PasswordStrength';
import { getFuncionarioTheme } from '@/lib/funcionarioTheme';

const COMMUNITIES = [
  {
    id: 'extremadura',
    label: 'Extremadura',
    flag: '🇪🇸',
    description: 'Gestión municipal y administración local',
  },
  {
    id: 'andalucia',
    label: 'Andalucía',
    flag: '🇪🇸',
    description: 'Administración pública andaluza',
  },
];

export function RegisterFuncionarioPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [municipalityName, setMunicipalityName] = useState('');
  const [autonomousCommunity, setAutonomousCommunity] = useState('extremadura');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signUpWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { isValid: isPasswordValid } = usePasswordValidation(password);

  const theme = getFuncionarioTheme(autonomousCommunity);

  const queryParams = new URLSearchParams(window.location.search);
  const sessionId = queryParams.get('session_id');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordValid) return toast.error('La contraseña no cumple los requisitos');
    if (!termsAccepted) return toast.error('Debes aceptar los términos');
    setIsLoading(true);
    try {
      const { error } = await signUpWithEmail(
        email.trim().toLowerCase(),
        password,
        fullName,
        'funcionario',
        municipalityName || undefined,
        autonomousCommunity,
        undefined,
        sessionId || undefined
      );
      if (error) toast.error(error.message || 'Error al registrar');
      else navigate('/auth/pending-confirmation', { state: { email, userType: 'funcionario' } });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    localStorage.setItem('munilex_auth_intent', 'funcionario');
    const { error } = await signInWithGoogle();
    if (error) toast.error(error.message || 'Error con Google');
  };

  return (
    <div className="relative overflow-hidden">
      {/* Community Background Pattern */}
      <div className={`absolute inset-0 opacity-[0.04] ${theme.motifClass}`} style={{ backgroundSize: '24px 24px' }} />

      <div className="relative">
        <Link to="/auth/funcionarios/login" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3">
          <ArrowLeft className="h-3 w-3" /> Volver al login
        </Link>

        {sessionId && (
          <div className="mb-6 p-4 rounded-xl border-2 border-green-500/20 bg-green-500/5 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-500">
            <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center shrink-0">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-green-700 dark:text-green-400">¡Pago verificado con éxito!</p>
              <p className="text-[11px] text-green-600/80 dark:text-green-400/60 leading-tight">Completa tu registro para activar tu suscripción Premium inmediatamente.</p>
            </div>
          </div>
        )}

        {/* Header with Community Theme */}
        <div className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl text-white text-sm font-bold"
              style={{ backgroundColor: theme.accent }}
            >
              F
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Crear cuenta — Funcionario</h2>
              <p className="text-sm text-muted-foreground">Para profesionales de la administración pública</p>
            </div>
          </div>
        </div>

        {/* Community Selector */}
        <div className="mb-5">
          <label className="block text-sm font-semibold mb-2">Comunidad Autónoma</label>
          <div className="grid grid-cols-2 gap-3">
            {COMMUNITIES.map((c) => {
              const isSelected = autonomousCommunity === c.id;
              const cTheme = getFuncionarioTheme(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setAutonomousCommunity(c.id)}
                  className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    isSelected
                      ? 'shadow-md scale-[1.02]'
                      : 'border-border hover:border-primary/50 hover:shadow-sm'
                  }`}
                  style={isSelected ? { borderColor: cTheme.accent, backgroundColor: cTheme.accentSoft } : {}}
                >
                  {isSelected && (
                    <div
                      className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full text-white text-[10px]"
                      style={{ backgroundColor: cTheme.accent }}
                    >
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{c.flag}</span>
                    <span className="font-semibold text-sm">{c.label}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{c.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-1.5">Nombre completo</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required
              className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5">Correo electrónico</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5">Contraseña</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-ring" />
            <PasswordStrength password={password} />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5">Nombre del ayuntamiento (opcional)</label>
            <input value={municipalityName} onChange={(e) => setMunicipalityName(e.target.value)}
              placeholder="Ej: Ayuntamiento de Madrid"
              className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>

          <div className="flex items-start gap-2 pt-1">
            <input id="terms-fn" type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-ring" />
            <label htmlFor="terms-fn" className="text-sm text-muted-foreground">
              Acepto los <a href="/terms" className="text-primary hover:underline">Términos</a> y la{' '}
              <a href="/privacy" className="text-primary hover:underline">Política de Privacidad</a>
            </label>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading || !isPasswordValid || !termsAccepted}>
            {isLoading ? 'Creando cuenta...' : 'Crear cuenta'}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <div className="flex-1 h-px bg-border" />
          <span className="text-sm text-muted-foreground">o</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <Button type="button" variant="outline" className="w-full" onClick={handleGoogleSignup}>
          Continuar con Google
        </Button>

        <p className="text-sm text-center text-muted-foreground mt-6">
          ¿Ya tienes cuenta?{' '}
          <Link to="/auth/funcionarios/login" className="text-primary hover:underline font-medium">Inicia sesión</Link>
        </p>
      </div>
    </div>
  );
}
